import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { Button, Flex, FormDateRangeInput } from '..'
import { DateRangeFilterDef } from './types'
import { isPresetSelected } from './dateRangePresets'

const RangeContainer = styled.div`
  padding: 0;
  display: flex;
  gap: 10px;
  min-height: 300px;
`

function DateRangeFilter({
  filterDef,
  onChange,
}: {
  filterDef: DateRangeFilterDef
  onChange?: () => void
}) {
  const { control, setValue, getValues } = useFormContext()
  const [lastValues, setLastValues] = React.useState<[string, string]>(['', ''])

  const startValue = getValues(filterDef.keyDateStart)
  const endValue = getValues(filterDef.keyDateEnd)

  const presetOptions = React.useMemo(() => {
    return (
      filterDef.presetDateRanges?.map((preset) => ({
        ...preset,
        isSelected: isPresetSelected(preset, [startValue, endValue]),
      })) ?? []
    )
  }, [startValue, endValue, filterDef.presetDateRanges])

  return (
    <>
      {presetOptions.length > 0 && (
        <Flex padding='16px 24px'>
          {presetOptions.map((preset) => (
            <Button
              size='medium'
              key={preset.label}
              onClick={() => {
                const { startDate, endDate } = preset.apply()
                setValue(filterDef.keyDateStart, startDate.toISOString())
                setValue(filterDef.keyDateEnd, endDate.toISOString())
              }}
              style={{
                border: preset.isSelected
                  ? '2px solid var(--brand-primary)'
                  : undefined,
                backgroundColor: preset.isSelected
                  ? 'var(--surface-primary)'
                  : undefined,
              }}
            >
              {preset.label}
            </Button>
          ))}
        </Flex>
      )}
      <RangeContainer data-testid='range-container'>
        <Controller
          name={filterDef.keyDateStart}
          control={control}
          render={({ field }) => {
            const value: [string, string] = [
              field.value ?? '',
              filterDef.keyDateEnd ? getValues(filterDef.keyDateEnd) : '',
            ]

            return (
              <FormDateRangeInput
                inline={true}
                input={false}
                value={value}
                style={{ flex: 1, border: '1px solid red' }}
                onChange={(val?: string[] | null) => {
                  if (!val) return;
                  
                  const dateStart = val[0]
                  const dateEnd = val[1]
                  
                  // Check if values actually changed to prevent infinite updates
                  if (dateStart === lastValues[0] && dateEnd === lastValues[1]) {
                    return;
                  }
                  
                  // Update last values for future comparison
                  setLastValues([dateStart || '', dateEnd || ''])

                  field.onChange(dateStart)

                  setValue(filterDef.keyDateEnd, dateEnd, {
                    shouldDirty: true,
                  })

                  if (onChange) onChange()
                }}
              />
            )
          }}
        />
      </RangeContainer>
    </>
  )
}

export default DateRangeFilter
